import { Module } from 'vuex';
import { RootState, RootGettersReturns, LoggedInUser } from '../types/index';
import {
  StageGettersKeys,
  StageMutationsKeys,
  StageMutationsPayload,
  StageActionsKeys,
  StageState,
  StageActionsPayload,
  StageType,
  Shout,
} from '../types/stageState';
import {
  StageUpdateEmit,
  SocketActionsKeys,
  PointerUpdateEmit,
  FocusUpdateEmit,
  ShoutEmit,
  VideoStateEmit,
  PointerOfferEmit,
  PointerOfferRejectEmit,
  PointerOfferAcceptEmit,
  PointerOfferReclaimEmit,
} from '../types/socket';
import { RaiseHand } from '@/utils/shouts';
import { InterfaceMutationsKeys } from '../types/interface';

const initState = () => {
  const state: StageState = {
    pointer: {
      x: 0,
      y: 0,
      state: 1,
    },
    stage: {
      x: 0,
      y: 0,
      zoom: 1,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    prevStageState: {
      x: 0,
      y: 0,
      zoom: 1,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    focus: null,
    modifedStage: true,
    shouts: [],
    unseenShouts: false,
    raisedHands: [],
    videoState: {},
    laserOffer: {
      isOffered: false,
      key: 'string',
    },
    laserOfferedTo: {
      isAccepted: false,
    },
  };
  return state;
};
const Stage: Module<StageState, RootState> = {
  state: {
    ...initState(),
  },
  actions: {
    [StageActionsKeys.SET_STAGE]: async (
      { commit, dispatch, rootGetters },
      payload: StageActionsPayload['SET_STAGE']
    ) => {
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const sidebarWidth = (rootGetters as RootGettersReturns).sidebarWidth;
      commit(StageMutationsKeys.SET_MODIFY_STAGE, true);
      commit(StageMutationsKeys.SET_STAGE, payload);
      const params: StageUpdateEmit = {
        type: StageType.Stage,
        params: [
          {
            ...payload,
            board_id: boardId,
            width: window.innerWidth - sidebarWidth,
            height: window.innerHeight,
          },
        ],
      };
      if (!payload.skipEmit && !(rootGetters as RootGettersReturns).isGuest) {
        commit(StageMutationsKeys.SET_FOCUS, { board_element: null });
        await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      }
    },
    [StageActionsKeys.SET_POINTER]: async (
      { commit, dispatch, rootGetters },
      payload: StageActionsPayload['SET_POINTER']
    ) => {
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const user = (rootGetters as RootGettersReturns).currentUser?.id;
      const params: PointerUpdateEmit = {
        type: StageType.Pointer,
        params: [{ ...payload, board_id: boardId, user: user as number }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(StageMutationsKeys.SET_POINTER, { ...params.params[0] });
    },
    [StageActionsKeys.SET_FOCUS]: async (
      { commit, dispatch, rootGetters, state },
      payload: StageActionsPayload['SET_FOCUS']
    ) => {
      const boardId = (rootGetters as RootGettersReturns).boardId;
      if (!payload.board_element && payload.withPrevState) {
        dispatch(StageActionsKeys.SET_STAGE, state.prevStageState);
      }
      commit(StageMutationsKeys.SET_FOCUS, payload);
      if (!payload.board_element) return;
      const params: FocusUpdateEmit = {
        type: StageType.Focus,
        params: [{ board_id: boardId, board_element: payload.board_element }],
      };
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
    },
    [StageActionsKeys.SEND_SHOUT]: async (
      { commit, dispatch, rootGetters },
      payload: StageActionsPayload['SEND_SHOUT']
    ) => {
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const params: ShoutEmit = {
        type: StageType.Shout,
        params: [{ board_id: boardId, message: payload, time: new Date().toISOString() }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      const currentUser = (rootGetters as RootGettersReturns).currentUser;
      if (currentUser) {
        let convertedShout: Shout = {
          message: payload,
          user: currentUser,
          time: new Date().toISOString(),
        };

        if (RaiseHand.isRaiseHand(convertedShout)) {
          convertedShout = RaiseHand.TextShoutToRaiseHand(convertedShout);
          commit(StageMutationsKeys.SET_RAISED_HAND_USER, { user: convertedShout.user, raised: convertedShout.raised });
        }
        commit(StageMutationsKeys.ADD_SHOUT, convertedShout);
      }
    },
    [StageActionsKeys.SET_VIDEO_STATE]: async (
      { dispatch, rootGetters },
      payload: StageActionsPayload['SET_VIDEO_STATE']
    ) => {
      const boardId = (rootGetters as RootGettersReturns).boardId;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const userId = (rootGetters as RootGettersReturns).currentUser!.id;
      const params: VideoStateEmit = {
        type: StageType.Video,
        params: [{ ...payload, board_id: boardId, user: userId }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
    },
    [StageActionsKeys.OFFER_POINTER]: async (
      { dispatch, commit, rootGetters },
      payload: StageActionsPayload['OFFER_POINTER']
    ) => {
      const user = (rootGetters as RootGettersReturns).laserOfferedTo.user as LoggedInUser;
      if (user) {
        await dispatch(StageActionsKeys.TAKEAWAY_POINTER);
      }
      const params: PointerOfferEmit = {
        type: 'offer_laser',
        params: [{ guestOrUser: payload }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(StageMutationsKeys.SET_LASER_OFFERED_TO, {
        user: payload,
        isAccepted: false,
      });
    },
    [StageActionsKeys.ACCEPT_POINTER]: async ({ dispatch, commit, rootGetters }) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const user = (rootGetters as RootGettersReturns).currentUser as LoggedInUser;
      const key = (rootGetters as RootGettersReturns).laserOffer.key;
      const params: PointerOfferAcceptEmit = {
        type: 'accept_laser',
        params: [{ guestOrUser: user, key }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);

      commit(StageMutationsKeys.SET_LASER_OFFER, {
        isOffered: false,
        key,
      });
      await dispatch(InterfaceMutationsKeys.CHANGE_LASER_ACTIVE, true);
    },
    [StageActionsKeys.DECLINE_POINTER]: async ({ dispatch, commit, rootGetters }) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const user = (rootGetters as RootGettersReturns).currentUser as LoggedInUser;
      const key = (rootGetters as RootGettersReturns).laserOffer.key;
      const params: PointerOfferRejectEmit = {
        type: 'reject_laser',
        params: [{ guestOrUser: user, key }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);

      commit(StageMutationsKeys.SET_LASER_OFFER, {
        isOffered: false,
        key: '',
      });
    },
    [StageActionsKeys.TAKEAWAY_POINTER]: async ({ dispatch, commit, rootGetters }) => {
      const user = (rootGetters as RootGettersReturns).laserOfferedTo.user as LoggedInUser;
      const params: PointerOfferReclaimEmit = {
        type: 'reclaim_laser',
        params: [{ guestOrUser: user }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(StageMutationsKeys.SET_LASER_OFFERED_TO, {
        isAccepted: false,
      });
    },
    [StageActionsKeys.SET_UNSEEN_SHOUTS]: ({ commit }, payload: StageActionsPayload['SET_UNSEEN_SHOUTS']) => {
      commit(StageMutationsKeys.SET_UNSEEN_SHOUTS, payload);
    },
    [StageActionsKeys.SOCKET_ON_ST0]: ({ commit }, payload: StageActionsPayload['SOCKET_ON_ST0']) => {
      commit(StageMutationsKeys.SET_POINTER, payload);
    },
    [StageActionsKeys.SOCKET_ON_ST1]: ({ commit }, payload: StageActionsPayload['SOCKET_ON_ST1']) => {
      commit(StageMutationsKeys.SET_MODIFY_STAGE, false);
      commit(StageMutationsKeys.SET_STAGE, payload);
      commit(StageMutationsKeys.SET_FOCUS, { board_element: null });
    },
    [StageActionsKeys.SOCKET_ON_ST2]: ({ commit, rootGetters }, payload: StageActionsPayload['SOCKET_ON_ST2']) => {
      const openRightSidebar = (rootGetters as RootGettersReturns).openRightSidebar;
      if (!openRightSidebar) {
        commit(StageMutationsKeys.SET_UNSEEN_SHOUTS, true);
      }

      let convertedShout: Shout = payload;

      if (RaiseHand.isRaiseHand(payload)) {
        convertedShout = RaiseHand.TextShoutToRaiseHand(payload);
        commit(StageMutationsKeys.SET_RAISED_HAND_USER, { user: convertedShout.user, raised: convertedShout.raised });
      }

      commit(StageMutationsKeys.ADD_SHOUT, convertedShout);
    },
    [StageActionsKeys.SOCKET_ON_ST3]: ({ commit }, payload: StageActionsPayload['SOCKET_ON_ST3']) => {
      commit(StageMutationsKeys.SET_FOCUS, { board_element: payload.element });
    },
    [StageActionsKeys.SOCKET_ON_ST4]: ({ commit }, payload: StageActionsPayload['SOCKET_ON_ST4']) => {
      commit(StageMutationsKeys.SET_VIDEO_STATE, payload);
    },
    [StageActionsKeys.SOCKET_ON_OFFER_LASER]: ({ commit }, payload: StageActionsPayload['SOCKET_ON_OFFER_LASER']) => {
      commit(StageMutationsKeys.SET_LASER_OFFER, {
        isOffered: true,
        isAccepted: false,
        key: payload.key,
      });
    },
    [StageActionsKeys.SOCKET_ON_RECLAIM_LASER]: async ({ commit, dispatch }) => {
      commit(StageMutationsKeys.SET_LASER_OFFER, {
        isOffered: false,
        isAccepted: false,
        key: '',
      });
      await dispatch(InterfaceMutationsKeys.CHANGE_LASER_ACTIVE, false);
    },
    [StageActionsKeys.SOCKET_ON_REJECT_LASER]: ({ commit }) => {
      commit(StageMutationsKeys.SET_LASER_OFFERED_TO, {
        isAccepted: false,
      });
    },
    [StageActionsKeys.SOCKET_ON_ACCEPT_LASER]: async (
      { commit, dispatch },
      payload: StageActionsPayload['SOCKET_ON_ACCEPT_LASER']
    ) => {
      await dispatch(InterfaceMutationsKeys.CHANGE_LASER_ACTIVE, false);
      commit(StageMutationsKeys.SET_LASER_OFFERED_TO, {
        user: payload.guestOrUser,
        isAccepted: true,
        key: payload.key,
      });
    },
    [StageActionsKeys.RESET_STATE]: ({ commit }) => {
      commit(StageMutationsKeys.STAGE_RESET_STATE);
    },
  },
  mutations: {
    [StageMutationsKeys.SET_STAGE]: (state, payload: StageMutationsPayload['SET_STAGE']) => {
      state.stage = payload;
    },
    [StageMutationsKeys.SET_POINTER]: (state, payload: StageMutationsPayload['SET_POINTER']) => {
      state.pointer = { ...payload };
    },
    [StageMutationsKeys.ADD_SHOUT]: (state, payload: StageMutationsPayload['ADD_SHOUT']) => {
      state.shouts.push(payload);
    },
    [StageMutationsKeys.SET_FOCUS]: (state, payload: StageMutationsPayload['SET_FOCUS']) => {
      if (payload.board_element) {
        state.prevStageState = { ...state.stage };
      }
      state.focus = payload.board_element;
    },
    [StageMutationsKeys.SET_VIDEO_STATE]: (state, payload: StageMutationsPayload['SET_VIDEO_STATE']) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const existing = state.videoState[payload.element] || [];
      const set = new Set(existing);
      set.add(payload.user);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.videoState[payload.element] = [...set];
    },
    [StageMutationsKeys.SET_LASER_OFFER]: (state, payload: StageMutationsPayload['SET_LASER_OFFER']) => {
      state.laserOffer = { ...payload };
    },
    [StageMutationsKeys.SET_LASER_OFFERED_TO]: (state, payload: StageMutationsPayload['SET_LASER_OFFERED_TO']) => {
      state.laserOfferedTo = { ...payload };
    },
    [StageMutationsKeys.SET_UNSEEN_SHOUTS]: (state, payload: StageMutationsPayload['SET_UNSEEN_SHOUTS']) => {
      state.unseenShouts = payload;
    },
    [StageMutationsKeys.SET_MODIFY_STAGE]: (state, payload: StageMutationsPayload['SET_MODIFY_STAGE']) => {
      state.modifedStage = payload;
    },
    [StageMutationsKeys.SET_RAISED_HAND_USER]: (state, payload: StageMutationsPayload['SET_RAISED_HAND_USER']) => {
      const modifedSet = new Set(state.raisedHands);
      if (payload.raised) {
        modifedSet.add(payload.user.id);
      } else {
        modifedSet.delete(payload.user.id);
      }
      state.raisedHands = Array.from(modifedSet);
    },
    [StageMutationsKeys.STAGE_RESET_STATE]: state => {
      Object.assign(state, initState());
    },
  },
  getters: {
    [StageGettersKeys.stageState]: state => state.stage,
    [StageGettersKeys.pointerState]: state => state.pointer,
    [StageGettersKeys.focusState]: state => state.focus,
    [StageGettersKeys.shouts]: state => state.shouts,
    [StageGettersKeys.unseenShouts]: state => state.unseenShouts,
    [StageGettersKeys.modifedStage]: state => state.modifedStage,
    [StageGettersKeys.raisedHand]: state => state.raisedHands,
    [StageGettersKeys.videoState]: state => state.videoState,
    [StageGettersKeys.laserOffer]: state => state.laserOffer,
    [StageGettersKeys.laserOfferedTo]: state => state.laserOfferedTo,
  },
};

export default Stage;
